// ── Embed ▾ control: image + generic-file picker dropdown (C1447) ──
//
// Mirrors the Task App's New Objective composer embed control (buildEmbedMenuHtml,
// ai/todo/server/src/client/chat-ui.js, C1241) — one paperclip trigger opening a two-row
// menu (Image / Other file) — adapted to the web frontend's plain-JS conventions (no
// i18n, no WS blob-swap optimistic preview). Ships together with the live thumbnail
// strip (lib/attachment-preview.js) since the two are always mounted/torn down as a pair.
//
// The dropdown is body-appended and fixed-positioned rather than laid out inline, same
// pattern as components/member-combo.js: both `.modal` (New Task modal) and
// `.task-detail-body` (panel mode) are `overflow-y: auto` clipping contexts, so an
// in-flow absolutely-positioned dropdown would be cut off near the bottom of a long form.

import { FILE_ACCEPT, uploadAttachmentFile } from './file-attach.js';
import { uploadImageFile } from './image-paste.js';
import { renderAttachmentStrip } from './attachment-preview.js';
import { escapeHtml } from '../utils.js';

// escapeHtml() escapes & < > via textContent->innerHTML but NOT '"'. Import row labels are
// written into a data-action="…" attribute's neighboring text content only (no quoting
// hazard there), but the action itself is also interpolated into an attribute below — same
// extra-escape need as member-combo.js's escapeAttr().
function escapeAttr(s) {
  return escapeHtml(s).replace(/"/g, '&quot;');
}

const PAPERCLIP_SVG = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21.44 11.05l-9.19 9.19a5.5 5.5 0 0 1-7.78-7.78l9.2-9.19a3.5 3.5 0 0 1 4.95 4.95l-9.2 9.19a1.5 1.5 0 0 1-2.12-2.12l8.49-8.48"/></svg>';
const IMPORT_SVG = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>';

// Module-level singleton — opening a new dropdown (Embed or Import, on this control or
// another mounted instance) closes any other open one. Also lets a stray outside-mousedown
// handler self-heal (`if (!block.isConnected)`) when a host tears down its DOM with the
// menu still open — e.g. closeTaskDetail() while mid-edit, which doesn't run this module's
// own destroy().
let _closeOpenMenu = null;

// createMenu(trigger, itemsHtml, onSelect) -> { close() }
// Shared dropdown machinery for both the Embed ▾ and Import ▾ triggers — body-appended +
// fixed-positioned submenu (both `.modal` and `.task-detail-body` are overflow:auto
// clipping contexts), reposition on scroll/resize, capture-phase Escape, deferred
// outside-mousedown close. Sharing one `_closeOpenMenu` singleton across both triggers
// (rather than giving Import its own module) means the two dropdowns can never be open
// at the same time.
function createMenu(trigger, scrollEl, itemsHtml, onSelect) {
  let submenu = null;
  let onDocMousedown = null;
  let onDocKeydown = null;
  let onReposition = null;

  function positionSubmenu() {
    if (!submenu) return;
    const r = trigger.getBoundingClientRect();
    submenu.style.top = `${r.bottom + 4}px`;
    submenu.style.left = `${r.left}px`;
  }

  function closeMenu() {
    if (_closeOpenMenu === closeMenu) _closeOpenMenu = null;
    if (submenu) { submenu.remove(); submenu = null; }
    if (onDocMousedown) { document.removeEventListener('mousedown', onDocMousedown); onDocMousedown = null; }
    if (onDocKeydown) { document.removeEventListener('keydown', onDocKeydown, true); onDocKeydown = null; }
    if (onReposition) {
      if (scrollEl) scrollEl.removeEventListener('scroll', onReposition, true);
      window.removeEventListener('resize', onReposition);
      onReposition = null;
    }
    trigger.setAttribute('aria-expanded', 'false');
  }

  function openMenu() {
    if (_closeOpenMenu) _closeOpenMenu();

    submenu = document.createElement('div');
    submenu.className = 'embed-submenu';
    submenu.innerHTML = itemsHtml;
    submenu.addEventListener('mousedown', (e) => e.preventDefault());
    submenu.addEventListener('click', (e) => {
      const item = e.target.closest('.embed-submenu-item');
      if (!item) return;
      // closeMenu() runs BEFORE onSelect() — load-bearing: guarantees this menu's capture
      // Escape handler is gone before onSelect (e.g. the Trello card picker) registers its
      // own, so a later Escape can't be swallowed by a stale handler here.
      closeMenu();
      onSelect(item.dataset.action);
    });
    document.body.appendChild(submenu);
    positionSubmenu();
    trigger.setAttribute('aria-expanded', 'true');

    // Deferred so the trigger's own mousedown (which is what opened this menu) doesn't
    // immediately register as an "outside" click and close it right back.
    setTimeout(() => {
      onDocMousedown = (e) => {
        if (!trigger.isConnected) { closeMenu(); return; }
        if (!trigger.contains(e.target) && !submenu?.contains(e.target)) closeMenu();
      };
      document.addEventListener('mousedown', onDocMousedown);
    }, 0);

    // Capture-phase + stopPropagation: ui.js's openModal() registers an unconditional
    // bubble-phase `Escape -> closeModal()` on document BEFORE any dropdown here exists
    // (it's added at open time, before onMount runs), so a bubble-phase handler added
    // later would never win the race. A capture-phase listener on document runs during
    // the capture pass, which completes in full before any bubble-phase listener (on any
    // target, including document itself) ever fires — stopPropagation here keeps the
    // event from ever reaching that later bubble-phase handler. First Escape closes the
    // menu; a second Escape (once the menu is already gone) closes the modal/panel.
    onDocKeydown = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        closeMenu();
      }
    };
    document.addEventListener('keydown', onDocKeydown, true);

    onReposition = () => positionSubmenu();
    if (scrollEl) scrollEl.addEventListener('scroll', onReposition, true);
    window.addEventListener('resize', onReposition);

    _closeOpenMenu = closeMenu;
  }

  trigger.addEventListener('click', () => {
    if (submenu) closeMenu();
    else openMenu();
  });

  return { close: closeMenu };
}

// attachEmbedControls(textarea, opts) -> { destroy(), refresh() }
//   projectId    -> number|string      required
//   taskKey      -> value|fn|null      same thunk contract as attachImageHandlers
//   anchor       -> Element            block is inserted immediately after this element
//                                      (default: textarea itself)
//   scrollEl     -> Element|Window     scroll container the dropdown repositions against
//   onPickerOpen -> fn|null            called right before the native file dialog opens
//   onPickerDone -> fn|null            called on picker change/cancel (dialog closed)
//   imports      -> [{action,label}]   optional — renders an "Import ▾" trigger when
//                                      non-empty (default []). Off by default so hosts
//                                      that don't pass it (task-detail.js) render exactly
//                                      today's single-trigger DOM.
//   onImport     -> fn(action)|null    called with the picked row's `action` (default null)
//   onUpload     -> fn({kind,url})|null  called after each successful picker upload — same
//                                      contract as image-paste.js's uploadImageFile/
//                                      uploadAttachmentFile (default null, no-op)
export function attachEmbedControls(textarea, opts = {}) {
  const {
    projectId, taskKey = null, anchor = textarea, scrollEl = null,
    onPickerOpen = null, onPickerDone = null, imports = [], onImport = null, onUpload = null,
  } = opts;

  const showImport = imports.length > 0 && typeof onImport === 'function';

  const block = document.createElement('div');
  block.className = 'embed-block';
  block.innerHTML = `
    <div class="embed-controls">
      <div class="embed-menu-wrap">
        <input type="file" class="embed-img-input" accept="image/*" multiple hidden>
        <input type="file" class="embed-file-input" accept="${FILE_ACCEPT}" multiple hidden>
        <button type="button" class="embed-menu-trigger" aria-haspopup="true" aria-expanded="false">
          ${PAPERCLIP_SVG}<span>Embed</span><span class="embed-menu-caret" aria-hidden="true">▾</span>
        </button>
      </div>
      ${showImport ? `
      <div class="embed-menu-wrap">
        <button type="button" class="embed-menu-trigger import-menu-trigger" aria-haspopup="true" aria-expanded="false">
          ${IMPORT_SVG}<span>Import</span><span class="embed-menu-caret" aria-hidden="true">▾</span>
        </button>
      </div>` : ''}
    </div>
    <div class="embed-thumb-strip"></div>`;
  anchor.insertAdjacentElement('afterend', block);

  const trigger = block.querySelector('.embed-menu-trigger');
  const importTrigger = block.querySelector('.import-menu-trigger');
  const imgInput = block.querySelector('.embed-img-input');
  const fileInput = block.querySelector('.embed-file-input');
  const strip = block.querySelector('.embed-thumb-strip');

  // Any mousedown inside this control (either trigger, either submenu's rows, strip remove
  // buttons) must never blur the textarea: task-detail.js's description blur handler is
  // {once:true} and destroys the textarea (replaceWith), and board.js's blur swaps in the
  // C1446 rendered preview — either would kill the upload target or the caret mid-click.
  block.addEventListener('mousedown', (e) => e.preventDefault());

  const embedMenu = createMenu(trigger, scrollEl, `
      <div class="embed-submenu-item" data-action="image">Image</div>
      <div class="embed-submenu-item" data-action="file">Other file</div>`,
    (action) => openPicker(action === 'image' ? imgInput : fileInput));

  const importMenu = showImport
    ? createMenu(importTrigger, scrollEl,
        imports.map(i => `<div class="embed-submenu-item" data-action="${escapeAttr(i.action)}">${escapeAttr(i.label)}</div>`).join(''),
        (action) => onImport(action))
    : null;

  function firePickerDone() {
    if (onPickerDone) onPickerDone();
  }

  // openPicker(input) — click()s a hidden file input with a fallback that guarantees
  // firePickerDone() fires even on engines with no 'cancel' event (pre-Chrome 113/Firefox
  // 118/Safari 16.4): those only ever get 'change' when a file was actually picked, so
  // dismissing the dialog with nothing selected would otherwise never fire it at all,
  // leaving a caller like task-detail.js's `pickerOpen` guard stuck permanently true. Both
  // 'change' and 'cancel' fire well before the window regains focus in every engine, so by
  // the time this fallback's deferred check runs, a real event has usually already called
  // firePickerDone() once — calling it again here is a harmless no-op (task-detail.js's
  // onPickerDone just clears the flag and refocuses).
  function openPicker(input) {
    if (onPickerOpen) onPickerOpen();
    let settled = false;
    const settle = () => { settled = true; };
    input.addEventListener('change', settle, { once: true });
    input.addEventListener('cancel', settle, { once: true });
    const onWindowFocus = () => {
      window.removeEventListener('focus', onWindowFocus);
      setTimeout(() => { if (!settled) firePickerDone(); }, 300);
    };
    window.addEventListener('focus', onWindowFocus);
    input.click();
  }

  imgInput.addEventListener('change', () => {
    [...imgInput.files].forEach(f => uploadImageFile(textarea, f, { projectId, taskKey, onUpload }));
    imgInput.value = '';
    firePickerDone();
  });
  imgInput.addEventListener('cancel', firePickerDone);
  fileInput.addEventListener('change', () => {
    [...fileInput.files].forEach(f => uploadAttachmentFile(textarea, f, { projectId, taskKey, onUpload }));
    fileInput.value = '';
    firePickerDone();
  });
  fileInput.addEventListener('cancel', firePickerDone);

  function refresh() {
    renderAttachmentStrip(strip, textarea);
  }
  textarea.addEventListener('input', refresh);
  refresh(); // picks up refs already in an existing description (task-detail re-edit)

  function destroy() {
    embedMenu.close();
    importMenu?.close();
    textarea.removeEventListener('input', refresh);
    block.remove();
  }

  return { destroy, refresh };
}

// Exported for hosts whose teardown path can't reliably fire this module's own destroy()
// (e.g. closeTaskDetail() closing a panel that's mid-edit without the textarea ever
// blurring) — call alongside the host's own cleanup to guarantee no dangling document
// listener survives.
export function closeAnyOpenEmbedMenu() {
  if (_closeOpenMenu) _closeOpenMenu();
}
